// Search input + 5 control surfaces for the meetings tab. Cadence /
// Status / Minutes are multi-select chips with listbox popovers; Date
// range is a single-select preset chip; Include archived is a binary
// toggle. Mirrors the deliverable FilterChipBar surface so the two
// tabs read with the same chip vocabulary.
//
// Active multi-select chips show a count badge in the brand-active
// palette (bg-brand-50 / border-brand-400). The single-select date
// chip surfaces the selected label rather than a count, and is treated
// as inactive when the value is the default 'all-time'.
//
// Search input debounces by 200ms; resetCounter forces the local draft
// to clear so the parent can synchronously reset the bar without
// waiting for the debounce window to flush.

import { useEffect, useRef, useState } from "react";
import type {
  MeetingDerivedStatus,
} from "../../../../types";
import DebouncedSearchInput from "../shared/DebouncedSearchInput";

export type MeetingCadenceFilter = "recurring" | "one-off";
export type MeetingMinutesFilter =
  | "with-minutes"
  | "awaiting-minutes"
  | "not-applicable";
export type MeetingDateRangeFilter =
  | "this-week"
  | "next-30-days"
  | "past-30-days"
  | "all-time";

export interface MeetingFilterChipBarFilters {
  cadence: MeetingCadenceFilter[];
  status: MeetingDerivedStatus[];
  minutes: MeetingMinutesFilter[];
  dateRange: MeetingDateRangeFilter;
  includeArchived: boolean;
}

export interface MeetingFilterChipBarProps {
  filters: MeetingFilterChipBarFilters;
  onFiltersChange: (next: MeetingFilterChipBarFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** Bump to force the local search-draft state to clear synchronously
   *  alongside any in-flight debounce — used by parent "Clear all". */
  resetCounter?: number;
}

interface OptionDef<V extends string> {
  value: V;
  label: string;
}

const CADENCE_OPTIONS: OptionDef<MeetingCadenceFilter>[] = [
  { value: "recurring", label: "Recurring" },
  { value: "one-off", label: "One-off" },
];

const STATUS_OPTIONS: OptionDef<MeetingDerivedStatus>[] = [
  { value: "scheduled", label: "Scheduled" },
  { value: "due-soon", label: "Due soon" },
  { value: "overdue", label: "Overdue" },
  { value: "held", label: "Held" },
  { value: "cancelled", label: "Cancelled" },
];

const MINUTES_OPTIONS: OptionDef<MeetingMinutesFilter>[] = [
  { value: "with-minutes", label: "With minutes" },
  { value: "awaiting-minutes", label: "Awaiting minutes" },
  { value: "not-applicable", label: "Not applicable" },
];

const DATE_RANGE_OPTIONS: OptionDef<MeetingDateRangeFilter>[] = [
  { value: "this-week", label: "This week" },
  { value: "next-30-days", label: "Next 30 days" },
  { value: "past-30-days", label: "Past 30 days" },
  { value: "all-time", label: "All time" },
];

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";

const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";

const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";

const POPOVER_CLASS =
  "absolute z-20 mt-1 min-w-[180px] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

interface MultiSelectChipProps<V extends string> {
  chipKey: "cadence" | "status" | "minutes";
  label: string;
  options: OptionDef<V>[];
  selected: V[];
  onToggle: (next: V[]) => void;
}

function MultiSelectChip<V extends string>({
  chipKey,
  label,
  options,
  selected,
  onToggle,
}: MultiSelectChipProps<V>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleToggleOption = (value: V) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onToggle(next);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
      >
        <span>{label}</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={`filter-popover-${chipKey}`}
          role="listbox"
          aria-multiselectable="true"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => handleToggleOption(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

interface SingleSelectChipProps<V extends string> {
  chipKey: "date-range";
  label: string;
  options: OptionDef<V>[];
  value: V;
  defaultValue: V;
  onChange: (next: V) => void;
}

function SingleSelectChip<V extends string>({
  chipKey,
  label,
  options,
  value,
  defaultValue,
  onChange,
}: SingleSelectChipProps<V>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = value !== defaultValue;
  const selectedLabel = options.find((o) => o.value === value)?.label ?? label;

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
      >
        <span>{label}</span>
        {isActive && <span className="text-brand-700">· {selectedLabel}</span>}
      </button>
      {open && (
        <div
          data-testid={`filter-popover-${chipKey}`}
          role="listbox"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="radio"
                      name={chipKey}
                      checked={value === opt.value}
                      onChange={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

interface BinaryChipProps {
  label: string;
  active: boolean;
  onToggle: () => void;
}

function BinaryChip({ label, active, onToggle }: BinaryChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={`${CHIP_BASE} ${active ? CHIP_ACTIVE : CHIP_INACTIVE}`}
    >
      {label}
    </button>
  );
}

export default function MeetingFilterChipBar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  resetCounter,
}: MeetingFilterChipBarProps) {
  const update = <K extends keyof MeetingFilterChipBarFilters>(
    key: K,
    value: MeetingFilterChipBarFilters[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DebouncedSearchInput
        value={search}
        onDebouncedChange={onSearchChange}
        resetCounter={resetCounter}
        ariaLabel="Search meetings"
        placeholder="Search meetings…"
      />

      <MultiSelectChip
        chipKey="cadence"
        label="Cadence"
        options={CADENCE_OPTIONS}
        selected={filters.cadence}
        onToggle={(next) => update("cadence", next)}
      />
      <MultiSelectChip
        chipKey="status"
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.status}
        onToggle={(next) => update("status", next)}
      />
      <MultiSelectChip
        chipKey="minutes"
        label="Minutes"
        options={MINUTES_OPTIONS}
        selected={filters.minutes}
        onToggle={(next) => update("minutes", next)}
      />
      <SingleSelectChip
        chipKey="date-range"
        label="Date range"
        options={DATE_RANGE_OPTIONS}
        value={filters.dateRange}
        defaultValue="all-time"
        onChange={(next) => update("dateRange", next)}
      />
      <BinaryChip
        label="Include archived"
        active={filters.includeArchived}
        onToggle={() => update("includeArchived", !filters.includeArchived)}
      />
    </div>
  );
}
